<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-bowl-mix-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <div v-else class="rd-card">
      <table class="rd-tbl">
        <thead>
          <tr>
            <th scope="col" class="rd-col-num">#</th>
            <th scope="col" :aria-sort="ariaSort('name')">
              <button type="button" @click="sortBy('name')">
                {{ isCompaniesTab ? $t("firm") : $t("meal") }} {{ sortArrow("name") }}
              </button>
            </th>
            <th v-if="isCompaniesTab" scope="col">{{ $t("report_desktop.source") }}</th>
            <th
              v-for="shift in visibleShifts"
              :key="shift.id"
              scope="col"
              class="is-right"
              :aria-sort="ariaSort(shift.key)"
            >
              <button type="button" @click="sortBy(shift.key)">{{ $t(shift.label) }} {{ sortArrow(shift.key) }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('total')">
              <button type="button" @click="sortBy('total')">{{ $t("total") }} {{ sortArrow("total") }}</button>
            </th>
            <th scope="col" class="rd-col-share">{{ $t("report_desktop.share") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="row.id || row.name">
            <td class="rd-num rd-muted">{{ index + 1 }}</td>
            <td class="is-name" :title="row.name">{{ row.name }}</td>
            <td v-if="isCompaniesTab">
              <span v-for="source in row.sources" :key="source" class="rd-status rd-status--muted rd-source">
                {{ sourceLabel(source) }}
              </span>
            </td>
            <td
              v-for="shift in visibleShifts"
              :key="shift.id"
              class="is-right rd-num"
              :class="{ 'is-zero': !row[shift.key] }"
            >
              {{ row[shift.key] }}
            </td>
            <td class="is-right rd-num"><b>{{ row.total }}</b></td>
            <td class="rd-col-share">
              <span class="rd-share">
                <span class="rd-mini"><i :style="{ width: `${row.relative}%` }"></i></span>
                <span class="rd-muted rd-num">{{ row.share }}%</span>
              </span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td>{{ $t("total") }}</td>
            <td v-if="isCompaniesTab"></td>
            <td v-for="shift in visibleShifts" :key="shift.id" class="is-right rd-num">
              {{ totals[shift.key] }}
            </td>
            <td class="is-right rd-num">{{ totals.total }}</td>
            <td class="rd-col-share"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import ReportTabAllCompaniesSummary from "@/components/report/tabs/ReportTabAllCompaniesSummary.vue";
import { SHIFTS } from "@/constants/shifts";
import hasPlural from "../../shared/hasPlural";
import hasShiftBinding from "../../shared/hasShiftBinding";
import hasShiftSubtitle from "../../shared/hasShiftSubtitle";
import hasTableSort from "../hasTableSort";
import ReportState from "../ReportState.vue";
import ReportTools from "../ReportTools.vue";
import { copyText, rowsToTsv } from "../copyTable";

/**
 * "Sve firme – zbirni izveštaj" na računaru (Obroci ili Firme): stavka × smena sa udjelom.
 * Nasleđuje učitavanje, štampu i Excel iz ReportTabAllCompaniesSummary.
 */
export default {
  name: "ReportDesktopAllSummary",
  extends: ReportTabAllCompaniesSummary,
  mixins: [hasPlural, hasShiftBinding, hasShiftSubtitle, hasTableSort],
  components: { ReportState, ReportTools },
  props: {
    /** "meals" ili "companies" */
    mode: {
      type: String,
      default: "meals",
    },
  },
  data() {
    return { tab: this.mode === "companies" ? 1 : 0 };
  },
  computed: {
    /** Smene 1–3 uvek, četvrta samo kad ima obroka. */
    visibleShifts() {
      return this.selectedShiftColumns.filter(
        (shift) => shift.id < 4 || this.visibleRows.some((row) => row[shift.key] > 0)
      );
    },
    rows() {
      const searched = this.customSearch(this.visibleRows, [{ value: "name" }], this.filters.search);
      const grand = searched.reduce((sum, row) => sum + row.total, 0);
      const max = Math.max(1, ...searched.map((row) => row.total));
      return this.sortRows(
        searched.map((row) => ({
          ...row,
          share: grand ? Math.round((row.total / grand) * 100) : 0,
          relative: Math.round((row.total / max) * 100),
        }))
      );
    },
    totals() {
      const totals = { total: this.rows.reduce((sum, row) => sum + row.total, 0) };
      SHIFTS.forEach((shift) => {
        totals[shift.key] = this.rows.reduce((sum, row) => sum + (row[shift.key] || 0), 0);
      });
      return totals;
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("report_mobile.no_meals");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "total", label: this.$t("report_desktop.total_meals"), value: this.totals.total, tone: "inverse" },
        {
          key: "count",
          label: this.isCompaniesTab ? this.$t("companies") : this.$t("meals"),
          value: this.rows.length,
        },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading(loading) {
      this.$emit("loading", loading);
    },
  },
  methods: {
    async copyTable() {
      const headers = [
        this.isCompaniesTab ? this.$t("firm") : this.$t("meal"),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("total"),
      ];
      const rows = this.rows.map((row) => [
        row.name,
        ...this.visibleShifts.map((shift) => row[shift.key]),
        row.total,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
